import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import './player.css';
import { Row, Col } from 'react-bootstrap';
import { Line } from 'rc-progress';
import UserHeader from '../UserHeader';
import { request } from '../utils';
import { highlightColumnColor, checkHealthScoreStatus, isPlayerFullyRecovered, showHealthyToReturnDate } from '../utils/dashboardCalculations';
import DashboardTip from './../Tooltip/dashboardTip';
import currentStatus from '../../global/img/1.png';
import expectedReturn from '../../global/img/2.png';
import fullRecovery from '../../global/img/3.png';
import gameMissed from '../../global/img/4.png';
import basketballIcon from '../../global/img/cricket.svg';
import booster from '../../global/img/booster.svg';
import boosterarrow from '../../global/img/booster-arrow.svg';
import PlayerTwitterFeed from './News/TwitterFeed';

const safeParseJson = (raw) => {
	try {
		if (!raw || typeof raw !== 'string') return raw || {};
		return JSON.parse(raw);
	} catch (e) {
		return {};
	}
};

const normalizePlayerTweet = (tweet) => {
	const tweetData = safeParseJson(tweet?.data);

	return {
		...tweet,
		tweet_id: tweet?.tweet_id || tweetData?.id || '',
		tweet_text: tweet?.tweet_text || tweetData?.text || '',
		tweet_date: tweet?.tweet_date || tweetData?.created_at || '',
		media_url: tweet?.media_url || ''
	};
};

const normalizePlayerTweetInfo = (raw) => {
	try {
		if (!raw) {
			return {
				twitter_handle: '',
				twitter_profile_image: '',
				tweets: []
			};
		}

		const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
		return {
			twitter_handle: value?.twitter_handle || value?.twitterHandle || '',
			twitter_profile_image: value?.twitter_profile_image || value?.twitterProfileImage || '',
			tweets: Array.isArray(value?.tweets) ? value.tweets.map(normalizePlayerTweet) : []
		};
	} catch (e) {
		return {
			twitter_handle: '',
			twitter_profile_image: '',
			tweets: []
		};
	}
};

function CricketPlayer(props) {
	const { user } = props;
	const { id } = useParams();
	const [state, setState] = useState({ loading: true, data: null, injuries: [], ipl: null, news: [], tweetInfo: { twitter_handle: '', twitter_profile_image: '', tweets: [] }, error: null, playerInfo: [], teamInfo: [] });
	const [visibleNewsCount, setVisibleNewsCount] = useState(6);
	const [activeVsTeamFormat, setActiveVsTeamFormat] = useState(null);
	const [activePointsFormat, setActivePointsFormat] = useState(null);

	useEffect(() => {
		const fetchPlayer = async () => {
			try {
				const res = await request({}, `/api/cricket/player/${id}`, 'GET', user?.token);
				const apiData = res?.data?.data || {};
				const stats = Array.isArray(apiData.playerStats) && apiData.playerStats.length > 0 ? apiData.playerStats[0] : null;
				const injuries = Array.isArray(apiData.injuries) ? apiData.injuries : [];
				const ipl = apiData.ipl || null;
				const news = Array.isArray(apiData.news) ? apiData.news : [];
				const playerInfo = Array.isArray(apiData.playerInfo) ? apiData.playerInfo : [];
				const teamInfo = Array.isArray(apiData.teamInfo) ? apiData.teamInfo : [];
				const tweetInfo = normalizePlayerTweetInfo(
					apiData.tweetInfo || apiData.playerTwitterInfo || apiData.twitterInfo || apiData.playerXInfo
				);
				setState({ loading: false, data: stats, injuries, ipl, news, tweetInfo, error: null, playerInfo, teamInfo });
			} catch (e) {
				setState({
					loading: false,
					data: null,
					injuries: [],
					ipl: null,
					news: [],
					tweetInfo: { twitter_handle: '', twitter_profile_image: '', tweets: [] },
					error: 'Unable to load player data'
				});
			}
		};
		fetchPlayer();
	}, [id, user?.token]);

	useEffect(() => {
		const handleScroll = () => {
			const newsSection = document.querySelector('.player-stats');
			if (newsSection) {
				const rect = newsSection.getBoundingClientRect();
				const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
				if (isVisible && window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200) {
					setVisibleNewsCount(prev => Math.min(prev + 2, 6));
				}
			}
		};
		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	if (state.loading) {
		return (
			<div id='fullHeight'>
				<div id='loading' />
			</div>
		);
	}

	const resolvePlayerName = (p) => {
		if (!p || typeof p !== 'object') return '';
		return p.short_name || p.nickname || p.player_name || p.full_name || p.name || '';
	};

	const player = state.data;
	const injuries = state.injuries || [];
	const ipl = state.ipl;
	const news = state.news || [];
	const tweetInfo = state.tweetInfo || { twitter_handle: '', twitter_profile_image: '', tweets: [] };
	const title = 'Cricket Player Profile';

	if (!player) {
		return (
			<div className='playerMainDiv'>
				<UserHeader matchup={true} player={true} backgroundColor={'#f7f7f7'} title={title} />
				<h1 style={{ textAlign: 'center' }}>No info found for player</h1>
			</div>
		);
	}

	const headerLogo = player.thumb_url || player.logo_url || '';
	const displayName = resolvePlayerName(player);
	const playerInfo = state?.playerInfo?.length ? state.playerInfo[0] : null;
	const hasTwitterFeed =
		Boolean(tweetInfo?.twitter_handle) ||
		Boolean(tweetInfo?.twitter_profile_image) ||
		(Array.isArray(tweetInfo?.tweets) && tweetInfo.tweets.length > 0);
	const playerHealthScore = playerInfo ? playerInfo.health_score : null;
	const teamHealthScore = playerInfo ? playerInfo.team_health_score : null;
	const healthStatus = checkHealthScoreStatus(playerHealthScore, null, 'player');
	const teamHealthStatus = checkHealthScoreStatus(teamHealthScore, null, 'team');
	const returnDateByInsideInjuries = playerInfo ? playerInfo.recovery_return_date : 'N/A';
	const teamEstimatedFullRecovery = playerInfo ? playerInfo.full_recovery_date_by_team : 'N/A';

	const tournamentSeries = Array.isArray(ipl?.tournamentSeries) ? ipl.tournamentSeries : [];
	const tournamentStatsRaw =
		(Array.isArray(ipl?.tournamentStats) && ipl.tournamentStats) ||
		(Array.isArray(ipl?.tournamentstats) && ipl.tournamentstats) ||
		[];
	const isIplTournament = (t) => {
		const n = (t?.title || t?.series_name || t?.tournament_name || t?.name || '').toString().toLowerCase();
		return n.includes('indian premier league') || n === 'ipl';
	};
	const iplTournamentStats = Array.isArray(tournamentStatsRaw) ? tournamentStatsRaw.filter(isIplTournament) : [];

	const run30Format = Array.isArray(ipl?.run30Format) ? ipl.run30Format : [];
	const gettingOut = Array.isArray(ipl?.gettingOut) ? ipl.gettingOut : [];
	const wicketsTakenAgainst = Array.isArray(ipl?.wicketsTakenAgainst) ? ipl.wicketsTakenAgainst : [];
	const wicketsTaken = Array.isArray(ipl?.wicketsTaken) ? ipl.wicketsTaken : [];

	const battingOrder = ipl?.battingOrder && typeof ipl.battingOrder === 'object' ? ipl.battingOrder : {};
	const teamsPlayedFor = Array.isArray(ipl?.teamsPlayedFor) ? ipl.teamsPlayedFor : [];
	const last10MatchesPoints = ipl?.last10MatchesPoints && typeof ipl.last10MatchesPoints === 'object' ? ipl.last10MatchesPoints : {};

	const now = new Date();
	const twelveMonthsAgo = new Date();
	twelveMonthsAgo.setFullYear(now.getFullYear() - 1);
	const fiveYearsAgo = new Date();
	fiveYearsAgo.setFullYear(now.getFullYear() - 5);

	const injuriesLast12Months = injuries.filter((inj) => {
		if (!inj.injury_date) return false;
		const d = new Date(inj.injury_date);
		if (Number.isNaN(d.getTime())) return false;
		return d >= twelveMonthsAgo && d <= now;
	});

	const injuriesLast5Years = injuries.filter((inj) => {
		if (!inj.injury_date) return false;
		const d = new Date(inj.injury_date);
		if (Number.isNaN(d.getTime())) return false;
		return d >= fiveYearsAgo && d <= now;
	});

	const formatInjuryDate = (dateStr) => {
		if (!dateStr) return 'No Recent Injury';
		const d = new Date(dateStr);
		if (Number.isNaN(d.getTime())) return 'No Recent Injury';
		return d.toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: '2-digit'
		});
	};

	const formatLongDate = (dateStr) => {
		if (!dateStr) return 'N/A';
		const d = new Date(dateStr);
		if (Number.isNaN(d.getTime())) return 'N/A';
		return d.toLocaleDateString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	};
const mapStatusToActive = (status) => {
	const inactiveStatuses = [
		'Doubtful',
		'Out',
		'Did Not Practice',
		'Suspended',
		'Physically Unable to Perform',
		'Injured Reserve',
		'Injured',
		'IR',
		'NFI'
	];
	console.log(status,'status')

	// Convert status to lowercase for case-insensitive comparison
	const normalizedStatus = status ? status.toLowerCase().trim() : '';
	
	return inactiveStatuses.includes(normalizedStatus) ? 'Inactive' : 'Active';
};
	const last10PointsT20i = Array.isArray(last10MatchesPoints?.t20i) ? last10MatchesPoints.t20i : [];
	const battingOrderT20i = battingOrder?.t20i;

	const runRange = ipl?.runRange && typeof ipl.runRange === 'object' ? ipl.runRange : {};
	const isFullyRecovered = isPlayerFullyRecovered(playerInfo);
	const hasCurrentReturnDate = Boolean(playerInfo?.recovery_return_date) && showHealthyToReturnDate(playerInfo.recovery_return_date, 3)
	const hasCurrentTeamRecoveryDate = Boolean(teamEstimatedFullRecovery) && showHealthyToReturnDate(teamEstimatedFullRecovery, 3) 
	
	const currentStatusLabel = isFullyRecovered ? 'Fully Recovered' : mapStatusToActive(player.status_injury || player.current_status || '');

	const teamDate = (playerInfo?.expected_return && String(playerInfo.expected_return).trim() !== '')
		? new Date(playerInfo.expected_return)
		: (playerInfo?.full_recovery_date_by_team ? new Date(playerInfo.full_recovery_date_by_team) : null);
	const algoDate = playerInfo?.recovery_return_date ? new Date(playerInfo.recovery_return_date) : null;
	let differenceInDays = 0;
	const bothDatesExist = teamDate && algoDate;

	if (bothDatesExist) {
		differenceInDays = Math.round((teamDate - algoDate) / (1000 * 60 * 60 * 24));
	}

	const grade = injuries.length > 0 ? injuries[0].final_injury_grade : null;
	const gradeRanges = {
		'0.5': [5, 4, 3, 2, -1, -2, -3, -4, -5],
		'1': [10, 8, 6, 4, -2, -4, -6, -8, -10],
		'2': [15, 12, 9, 6, -3, -6, -9, -12, -15],
		'3': [25, 20, 15, 10, -5, -10, -15, -20, -25],
		'4': [50, 40, 30, 20, -10, -20, -30, -40, -50],
		'5': [105, 90, 60, 45, -30, -45, -60, -90, -105]
	};

	function getArrowPositionIndex(grade, diff) {
		if (isFullyRecovered || diff === 0) {
			return 5;
		}
		if (Math.abs(diff) >= 182) return diff < 0 ? 10 : 0;

		let g = parseFloat(grade);
		if (!gradeRanges[g]) {
			g = Object.keys(gradeRanges).map(Number).reduce((prev, curr) =>
				Math.abs(curr - g) < Math.abs(prev - g) ? curr : prev
			);
		}
		const ranges = gradeRanges[g];

		if (Math.abs(diff) <= Math.abs(ranges[4])) {
			return 5;
		}
		if (diff < 0) {
			for (let i = 5; i <= 8; i++) {
				if (diff >= ranges[i]) return i;
			}
			return 10;
		}

		if (diff > 0) {
			for (let i = 0; i <= 3; i++) {
				if (diff >= ranges[i]) return i;
			}
			return 0;
		}

		return diff < 0 ? 10 : 0;
	}

	const arrowPosIndex = bothDatesExist ? getArrowPositionIndex(grade, differenceInDays) : 4;
	const arrowClass = bothDatesExist ? `boosterarrow pos-${arrowPosIndex}` : 'boosterarrow';

	return (
		<section className='playerMainSection'>
			<div className='playerMainDiv'>
				<UserHeader matchup={true} player={true} backgroundColor={'#f7f7f7'} title={title} />

				<div className='playerInfoRow'>
					<div className='container'>
						<Row className='playerProfileOverviewRow align-items-center'>
							<Col lg={12} md={12} sm={12} className='player-details text-center mb-3 mb-lg-0'>
								<figure className='player_logo text-center mb-2'>
									{headerLogo && (
										<img src={headerLogo} alt={displayName} className='playerInfoImg' />
									)}
								</figure>
								<div className='player_name'>
									<h2 className='h3 mb-0'>
										{displayName}
										{player.playing_role ? ` (${player.playing_role.toUpperCase()})` : ''}
									</h2>
								</div>
							</Col>
							<Col lg={12} md={12} sm={12}>
								<div className='playerInfoDiv'>
									<div className='d-flex flex-wrap justify-content-start align-items-center playerInfoStatsDiv'>
										<span className='playerInfoStatsTitle'><strong>NATIONALITY:</strong> {player.nationality || 'N/A'}</span>
										<span className='playerInfoStatsTitle'><strong>PLAYING ROLE:</strong> {player.playing_role || 'N/A'}</span>
										<span className='playerInfoStatsTitle'><strong>BATTING STYLE:</strong> {player.batting_style || 'N/A'}</span>
										<span className='playerInfoStatsTitle'><strong>BOWLING STYLE:</strong> {player.bowling_style || 'N/A'}</span>
										<span className='playerInfoStatsTitle'><strong>TOTAL MATCHES:</strong> {player.total_matches_played != null ? player.total_matches_played : 'N/A'}</span>
									</div>
								</div>
							</Col>
						</Row>
						<Row className='playerTotalInjuryRow container-fluid'>
							<Col lg={6} className='playerTotalInjuryCol'>
								<div className='playerTotalInjuryDiv'>
									<p className='playerTotalInjury'><strong>TEAM:</strong></p>
									<p className='playerTotalInjuryValue'>
										{player.team_name || 'N/A'} ({player.team_code || ''})
									</p>
								</div>
							</Col>
							<Col lg={6} className='playerTotalInjuryCol'>
								<div className='playerTotalInjuryDiv'>
									<p className='playerTotalInjury'><strong>DATE OF BIRTH:</strong></p>
									<p className='playerTotalInjuryValue'>
										{player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString('en-US') : 'N/A'}
									</p>
								</div>
							</Col>
						</Row>

						<div className='player-main'>
							<div className='player-left'>
								<ul className='list-unstyled current_status w-100 text-start mt-3'>
									<li>
										<em className='me-2'><img src={currentStatus} width='32' alt='' /></em>
										Current Status: {currentStatusLabel}
									</li>
									<li>
										<em className='me-2'><img src={expectedReturn} width='32' alt='' /></em>
										Expected Return By Inside Injuries: {isFullyRecovered ? 'N/A (Fully Recovered)' : (hasCurrentReturnDate ? formatLongDate(returnDateByInsideInjuries) : 'N/A')}
									</li>
									<li>
										<em className='me-2'><img src={fullRecovery} width='32' alt='' /></em>
										Estimated Full Recovery By Team: {isFullyRecovered ? 'N/A (Fully Recovered)' : (hasCurrentTeamRecoveryDate ? formatLongDate(teamEstimatedFullRecovery) : 'N/A')}
									</li>
									<li>
										<em className='me-2'><img src={basketballIcon} width='32' alt='' /></em>
										Games Missed This Season: N/A
									</li>
								</ul>

								<div className='w-100 text-center mt-4 playerInfoProgressDiv'>
									<h6 className='playerInfoProgressLabel'>
										Player Health Score:{' '}
										<span className={`playerInfoProgressValue ${healthStatus.color}`}>{healthStatus.status}</span>
									</h6>
									<div className='d-flex flex-wrap flex-md-nowrap justify-content-center playerInfoProgressBarDiv'>
										<div className='w-100 progressbar_elem displayTableCell'>
											<div
												className='progressbar_div'
												role='progressbar'
												aria-valuemin={0}
												aria-valuemax={100}
												aria-valuenow={Math.min(playerHealthScore, 100)}
												aria-label='Player Health Score'
											>
												<div className='progressbar_div_track'>
													<div
														className='progressbar_div_fill'
														style={{
															width: `${Math.min(playerHealthScore, 100)}%`,
															backgroundColor: highlightColumnColor(playerHealthScore, 'cardSpread', 'playerhealth', true)
														}}
													/>
												</div>
											</div>
										</div>
										<div className='d-flex align-items-center mx-3 progress_value'>
											<span className='playerInfoProgressBarValue displayTableCell'><strong>{playerHealthScore}/100</strong></span>
											<span className='d-inline-flex justify-content-center align-items-center info_icon'>
												<DashboardTip title='PLAYER HEALTH SCORE' description={'Health score represents the overall impact of a players\' past injuries to give an overall performance ranking'} component={'player_health_score'} />
											</span>
										</div>
									</div>
								</div>

								<div className='w-100 text-center mt-3 playerInfoProgressDiv'>
									<h6 className='playerInfoProgressLabel'>
										Team Health Score:{' '}
										<span className={`playerInfoProgressValue ${teamHealthStatus.color}`}>{teamHealthStatus.status}</span>
									</h6>
									<div className='d-flex flex-wrap flex-md-nowrap justify-content-center playerInfoProgressBarDiv'>
										<div className='w-100 progressbar_elem displayTableCell'>
											<Line
												percent={(teamHealthScore / 10) * 100}
												strokeWidth='4'
												trailWidth='4'
												trailColor='rgba(21, 21, 21, .06)'
												strokeColor={highlightColumnColor(teamHealthScore, 'cardSpread', 'teamhealth', true)}
											/>
										</div>
										<div className='d-flex align-items-center mx-3 progress_value'>
											<span className='playerInfoProgressBarValue displayTableCell'><strong>{teamHealthScore}/10</strong></span>
											<span className='d-inline-flex justify-content-center align-items-center info_icon'>
												<DashboardTip title='TEAM HEALTH SCORE' description={'Team health score represents the overall impact of all starting players\' health scores to give an overall performance ranking'} component={'team_health_score'} />
											</span>
										</div>
									</div>
								</div>
								{injuriesLast5Years.length > 0 && (
									<>
										<Row className='playerTotalInjuryRow container-fluid'>
											<Col lg={6} className='playerTotalInjuryCol'>
												<div className='playerTotalInjuryDiv'>
													<p className='playerTotalInjury'><strong>TOTAL INJURIES LAST 12 MONTHS:</strong></p>
													<p className='playerTotalInjuryValue'>{injuriesLast12Months.length}</p>
												</div>
											</Col>
											<Col lg={6} className='playerTotalInjuryCol'>
												<div className='playerTotalInjuryDiv'>
													<p className='playerTotalInjury'><strong>TOTAL INJURIES LAST 5 YEARS:</strong></p>
													<p className='playerTotalInjuryValue'>{injuriesLast5Years.length}</p>
												</div>
											</Col>
										</Row>
										<div className='playerHistoryDiv'>
											<p className='playerTotalInjuryHistory'>TOTAL INJURIES LAST 5 YEARS:</p>
											{injuriesLast5Years.map((history, i) => (
												<Row key={i} className='playerHistoryRow'>
													<Col lg={4} className='playerHistoryCol'>
														<p className='playerHistoryLabel'>INJURY</p>
														<p className='playerHistoryValue'>{history.body_part}</p>
													</Col>
													<Col lg={4} className='playerHistoryCol'>
														<p className='playerHistoryLabel'>INJURY GRADE</p>
														<p className='playerHistoryValue'>{history.admin_injury_grade || history.final_injury_grade}</p>
													</Col>
													<Col lg={4} className='playerHistoryCol'>
														<p className='playerHistoryLabel'>DATE</p>
														<p className='playerHistoryValue'>{formatInjuryDate(history.injury_date)}</p>
													</Col>
												</Row>
											))}
										</div>
									</>
								)}
							</div>
							<div className='player-right'>
								<h3>FIB-O-Meter</h3>
								<div className='booster'>
									<img src={booster} width='400' alt='booster' />
									<img className={arrowClass} src={boosterarrow} height='170' alt='boosterarrow' />
								</div>
								<p>This meter compares our algorithm's predicted healthy return date with the team's reported return date.</p>
								<p>It helps identify whether a team may be:</p>
								<ul>
									<li>Accurate - Aligning with data</li>
									<li>Overcautious - Delaying a return unnecessarily</li>
									<li>Risky or Misleading - Rushing a return or providing inaccurate timelines</li>
								</ul>
							</div>
						</div>

						{/* {tournamentSeries.length > 0 && (
							<div className='player-stats' style={{ marginTop: '20px' }}>
								<p className='playerTotalInjury'><strong>CURRENT SERIES</strong></p>
								<ul className='list-unstyled'>
									{tournamentSeries.map((name, index) => (
										<li key={index} className='playerCurrentSeriesItem'>
											{name}
										</li>
									))}
								</ul>
							</div>
						)} */}

						{iplTournamentStats.length > 0 && (
							<div className='latest-section player-stats'>
								<p className='playerTotalInjury latest-heading'><strong>Latest Tournament Stats</strong></p>
								<div className='table-responsive'>
									<table className='table table-striped table-bordered mb-0'>
										<thead>
											<tr>
												<th rowSpan={2} style={{ verticalAlign: 'middle' }}>Tournament</th>
												<th colSpan={8} className='text-center'>Batting</th>
												<th colSpan={6} className='text-center'>Bowling</th>
											</tr>
											<tr>
												<th>M</th>
												<th>Inns</th>
												<th>Runs</th>
												<th>HS</th>
												<th>Avg</th>
												<th>SR</th>
												<th>50</th>
												<th>100</th>
												<th>Ovs</th>
												<th>Runs</th>
												<th>Wkts</th>
												<th>Avg</th>
												<th>Econ</th>
												<th>SR</th>
											</tr>
										</thead>
										<tbody>
											{iplTournamentStats.map((t, idx) => {
												const batting = t?.batting || {};
												const bowling = t?.bowling || {};
												return (
													<tr key={idx}>
														<td>{t?.title || t?.name || '--'}</td>
														<td>{batting.matches ?? '--'}</td>
														<td>{batting.innings ?? '--'}</td>
														<td>{batting.runs ?? '--'}</td>
														<td>{batting.highest ?? '--'}</td>
														<td>{batting.average ?? '--'}</td>
														<td>{batting.strike ?? '--'}</td>
														<td>{batting.run50 ?? '--'}</td>
														<td>{batting.run100 ?? '--'}</td>
														<td>{bowling.overs ?? '--'}</td>
														<td>{bowling.runs ?? '--'}</td>
														<td>{bowling.wickets ?? '--'}</td>
														<td>{bowling.average ?? '--'}</td>
														<td>{bowling.econ ?? '--'}</td>
														<td>{bowling.strike ?? '--'}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							</div>
						)}
						<Row className="column-container">
							<Col lg={3} md={6} sm={12} className="first-column">
							{gettingOut.length > 0 && (
								<div className='player-stats'>
									<div className='playerTotalInjuryDiv'>
										<div className="header-with-svg">
										<svg version="1.2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="50" height="50">
											<defs>
												<image  width="92" height="90" id="img1" href="data:image/png;base64,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"/>
											</defs>
											<style>
											</style>
											<use id="Background" href="#img1" x="4" y="5"/>
										</svg>
										<p className='playerTotalInjury'><strong>Dismissals by<br/>Bowling Type</strong></p>
										</div>
										<div className='table-responsive'>
											<table className='table table-striped table-bordered mb-4'>
												<thead>
													<tr>
														<th>Bowling Style</th>
														<th>Dismissals</th>
													</tr>
												</thead>
												<tbody>
													{gettingOut.map((row, idx) => (
														<tr key={idx}>
															<td>{row.bowling_style}</td>
															<td>{row.count}</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								</div>
							)}
							</Col>
							<Col lg={3} md={6} sm={12} className="second-column">
								{wicketsTakenAgainst.length > 0 && (
									<div className='player-stats'>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury'><strong>Wickets by Batting Style</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-4'>
													<thead>
														<tr>
															<th>Batting Style</th>
															<th>Wickets</th>
														</tr>
													</thead>
													<tbody>
														{wicketsTakenAgainst.map((row, idx) => (
															<tr key={idx}>
																<td>{row.batting_style}</td>
																<td>{row.count}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}

								{run30Format.some((row) => (row?.type || '').toLowerCase() === 't20i') && (
									<div className='player-stats' style={{ marginTop: '20px' }}>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury'><strong>30+ Scores by<br/> Competition</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-4'>
													<thead>
														<tr>
															<th>Format</th>
															<th>30+ Scores</th>
														</tr>
													</thead>
													<tbody>
														{run30Format
															.filter((row) => (row?.type || '').toLowerCase() === 't20i')
															.map((row, idx) => (
																<tr key={idx}>
																	<td>IPL</td>
																	<td>{row.run30}</td>
																</tr>
															))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}

								{wicketsTaken.length > 0 && (
									<div className='player-stats' style={{ marginTop: '30px' }}>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury'><strong>WICKETS TAKEN (MATCHES)</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-4'>
													<thead>
														<tr>
															<th>Wickets in Innings</th>
															<th>Matches</th>
														</tr>
													</thead>
													<tbody>
														{wicketsTaken.map((row, idx) => (
															<tr key={idx}>
																<td>{row.wickets}</td>
																<td>{row.count}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}

							</Col>
							<Col lg={3} md={6} sm={12} className="third-column">
								{runRange && Object.keys(runRange).length > 0 && (
									<div className='player-stats'>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury'><strong>Run Range Distribution</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-4'>
													<thead>
														<tr>
															<th>Range</th>
															<th>Format</th>
															<th>Innings</th>
														</tr>
													</thead>
													<tbody>
														{Object.entries(runRange).map(([rangeKey, arr]) => {
															const filtered = Array.isArray(arr) ? arr.filter((row) => (row?.type || '').toLowerCase() === 't20i') : [];
															if (filtered.length === 0) return null;
															return filtered.map((row, idx) => (
																<tr key={`${rangeKey}-${idx}`}>
																	<td>{rangeKey.replace('run', '').replace('-', ' - ')}</td>
																	<td>IPL</td>
																	<td>{row.count}</td>
																</tr>
															));
														})}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}
							</Col>
							<Col lg={3} md={6} sm={12} className="fourth-column">
								{last10PointsT20i.length > 0 && (
									<div className='player-stats'>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury mb-0 me-3'><strong>Last 10 Matches - <br/>Fantasy Points</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-0'>
													<thead>
														<tr>
															<th>Match ID</th>
															<th>Points</th>
														</tr>
													</thead>
													<tbody>
														{last10PointsT20i.map((row, idx) => (
															<tr key={idx}>
																<td>{row.match_id}</td>
																<td>{row.point}</td>
															</tr>
														))}
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}

								{battingOrderT20i != null && battingOrderT20i !== '' && battingOrderT20i !== '0' && (
									<div className='player-stats' style={{ marginTop: '30px' }}>
										<div className='playerTotalInjuryDiv'>
											<p className='playerTotalInjury'><strong>Average Batting Position</strong></p>
											<div className='table-responsive'>
												<table className='table table-striped table-bordered mb-4'>
													<thead>
														<tr>
															<th>Format</th>
															<th>Position</th>
														</tr>
													</thead>
													<tbody>
														<tr>
															<td>IPL</td>
															<td>{battingOrderT20i}</td>
														</tr>
													</tbody>
												</table>
											</div>
										</div>
									</div>
								)}
							</Col>
						</Row>

						{/* {teamsPlayedFor.length > 0 && (
							<div className='player-stats' style={{ marginTop: '30px' }}>
								<div className='playerTotalInjuryDiv'>
									<p className='playerTotalInjury'><strong>TEAMS PLAYED FOR</strong></p>
									<div className='d-flex flex-wrap'>
										{teamsPlayedFor.map((team, idx) => (
											<div key={idx} className='text-center' style={{ margin: '10px', width: '120px' }}>
												{team.logo_url && (
													<img
														src={team.logo_url}
														alt={team.title}
														style={{ width: '50px', height: '50px', objectFit: 'contain' }}
													/>
												)}
												<p style={{ fontSize: '12px' }}>{team.title}</p>
											</div>
										))}
									</div>
								</div>
							</div>
						)} */}

						{(news.length > 0 || hasTwitterFeed) && (
							<div className="news-container">
								{news.length > 0 && (
								<div className='news-column aggregated player-stats'>
									<div className="header-with-svg">
										<svg fill="#000000" width="40px" height="40px" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"><title>ionicons-v5_logos</title><path d="M108.56,342.78a60.34,60.34,0,1,0,60.56,60.44A60.63,60.63,0,0,0,108.56,342.78Z"/><path d="M48,186.67v86.55c52,0,101.94,15.39,138.67,52.11s52,86.56,52,138.67h86.66C325.33,312.44,199.67,186.67,48,186.67Z"/><path d="M48,48v86.56c185.25,0,329.22,144.08,329.22,329.44H464C464,234.66,277.67,48,48,48Z"/></svg>
										<p className='large-heading playerTotalInjury'><strong>Aggregated IBL News</strong></p>
									</div>
									<ul className='news-column aggregated list-unstyled'>
										{news.slice(0, Math.min(visibleNewsCount, 6)).map((item, index) => (
											<li key={index} className='news-post playerNewsItem'>
												<div className="player-inner">
													<p className='playerNewsDate'>
														{item.Updated ? new Date(item.Updated).toLocaleDateString() : ''}
													</p>
													<p className='playerNewsTitle' style={{ fontWeight: 'bold', marginBottom: '5px' }}>
														{item.Title}
													</p>
													
													{item.Content ? (
														<p className='playerNewsContent' style={{ marginBottom: 0 }}>
															{item.Content}
														</p>
													) : null}
												</div>
											</li>
										))}
									</ul>
								</div>
								)}
								{hasTwitterFeed && (
									<PlayerTwitterFeed
										tweets={tweetInfo.tweets}
										twitter_handle={tweetInfo.twitter_handle}
										twitter_profile_image={tweetInfo.twitter_profile_image}
										tweets_per_page={6}
									/>
								)}
								{false && (
								<div className="news-column twitter-feed">
									<div className="news-header">
										<img
										src="https://img.icons8.com/ios-filled/24/twitterx--v2.png" alt="X logo" />
										<h2>@RoquanSmith1</h2>
									</div>
									<div className="news-post">
										<div className="post-profile">
										<img
											src="https://nitter.net/pic/profile_images%2F996494135560318976%2FpMcitFXF_bigger.jpg"
											alt="profile"
											width="50px"
										/>
										</div>
										<div className="news-body">
											<div className="post-header">
												<p>
												<strong className="username">RoquanSmith1</strong>
												<span className="postdate"> · 7/13/2019</span>
												</p>
											</div>
											<div className="post-body">
												<p>
												@The_Duffer_Bros I am now officially convinced that Jim will be
												rescued by the US GOVT in the upsidedown and asked to reenlist and
												battle another alien because of his monster battling track record.
												#StrangerThings #Predator
												</p>
											</div>
										</div>
									</div>
									<div className="news-post">
										<div className="post-profile">
										<img
											src="https://nitter.net/pic/profile_images%2F996494135560318976%2FpMcitFXF_bigger.jpg"
											alt="profile"
											width="50px"
										/>
										</div>
										<div className="news-body">
											<div className="post-header">
												<p>
												<strong className="username">RoquanSmith1</strong>
												<span className="postdate"> · 7/13/2019</span>
												</p>
											</div>
											<div className="post-body">
												<p>
												@The_Duffer_Bros I am now officially convinced that Jim will be
												rescued by the US GOVT in the upsidedown and asked to reenlist and
												battle another alien because of his monster battling track record.
												#StrangerThings #Predator
												</p>
											</div>
										</div>
									</div>
									<div className="news-post">
										<div className="post-profile">
										<img
											src="https://nitter.net/pic/profile_images%2F996494135560318976%2FpMcitFXF_bigger.jpg"
											alt="profile"
											width="50px"
										/>
										</div>
										<div className="news-body">
											<div className="post-header">
												<p>
												<strong className="username">RoquanSmith1</strong>
												<span className="postdate"> · 7/13/2019</span>
												</p>
											</div>
											<div className="post-body">
												<p>
												@The_Duffer_Bros I am now officially convinced that Jim will be
												rescued by the US GOVT in the upsidedown and asked to reenlist and
												battle another alien because of his monster battling track record.
												#StrangerThings #Predator
												</p>
											</div>
										</div>
									</div>
									<div className="news-post">
										<div className="post-profile">
										<img
											src="https://nitter.net/pic/profile_images%2F996494135560318976%2FpMcitFXF_bigger.jpg"
											alt="profile"
											width="50px"
										/>
										</div>
										<div className="news-body">
											<div className="post-header">
												<p>
												<strong className="username">RoquanSmith1</strong>
												<span className="postdate"> · 7/13/2019</span>
												</p>
											</div>
											<div className="post-body">
												<p>
												@The_Duffer_Bros I am now officially convinced that Jim will be
												rescued by the US GOVT in the upsidedown and asked to reenlist and
												battle another alien because of his monster battling track record.
												#StrangerThings #Predator
												</p>
											</div>
										</div>
									</div>
								</div>
								)}

							</div>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}

const mapStateToProps = (state) => ({
	user: state.user
});

export default connect(mapStateToProps)(CricketPlayer);
